import ActivityIcon from '@/Components/Icons/ActivityIcon'
import DashIcon from '@/Components/Icons/DashIcon'
import InventoryIcon from '@/Components/Icons/InventoryIcon'
import TransfersIcon from '@/Components/Icons/TransfersIcon'
import CustomersIcon from '@/Components/Icons/CustomersIcon'
import OrdersIcon from '@/Components/Icons/OrdersIcon'
import SettingsIcon from '@/Components/Icons/SettingsIcon'
import IntegrationsIcon from '@/Components/Icons/IntegrationsIcon'
import ReportingIcon from '@/Components/Icons/ReportingIcon'
import SupportIcon from '@/Components/Icons/SupportIcon'
import { WrenchScrewdriverIcon } from '@heroicons-2/vue/24/outline'
import { ROLE_ADMIN, ROLE_MANAGER, ROLE_STAKEHOLDER } from '@/utils/roles'

/**
 * Generates an array of page links accessible via the sidebar
 * @param {function} route the function we use to generate urls
 * @param {object} context contains page data such as current user and current company
 * @returns array
 */
function navConfig(route, context) {
  /**
   * Returns true if current user is company admin
   * @returns {boolean}
   */
  let isAdmin = () => context?.user?.system_role === ROLE_ADMIN
  let isCompanyAdminOrManager = () => context?.company_role === ROLE_ADMIN || context?.company_role === ROLE_MANAGER
  let isCompanyStakeholder = () => context?.company_role === ROLE_STAKEHOLDER
  let isCompanyAdmin = () => context?.company_role === ROLE_ADMIN
  let isStory = () => context?.company?.id === 64

  let hasPermission = (permission) => {
    return context?.company_permissions.findIndex((i) => i === permission) >= 0
  }

  return [
    {
      name: 'Design System',
      href: route('design-system'),
      route: 'design-system',
      icon: WrenchScrewdriverIcon,
      visible: isAdmin()
    },
    {
      name: 'Dashboard',
      href: route('dashboard'),
      route: 'dashboard',
      icon: DashIcon
    },
    {
      name: 'Activity Log',
      href: route('activity-log'),
      route: 'activity-log',
      icon: ActivityIcon,
      visible: isCompanyAdmin()
    },
    {
      name: 'Inventory',
      icon: InventoryIcon,
      children: [
        {
          name: 'Products',
          href: route('products.index'),
          route: 'products.index'
        },
        {
          name: 'Packages',
          href: route('packages.index'),
          route: 'packages.index'
        },
        {
          name: 'Approvals',
          href: route('packages.approvals.index'),
          route: 'packages.approvals.index'
        },
        {
          name: 'Counts',
          href: route('counts.index'),
          route: 'counts.index'
        },
        {
          name: 'Destructions',
          href: route('destructions.index'),
          route: 'destructions.index',
          visible: hasPermission('view_destructions')
        },
        {
          name: 'Track & Trace',
          href: route('integrations.biotrack.inventory.show'),
          route: 'integrations.biotrack.inventory.show'
        }
      ],
      visible: !isCompanyStakeholder()
    },
    {
      name: 'Transfers',
      icon: TransfersIcon,
      children: [
        {
          name: 'Standard',
          href: route('transfers.index'),
          route: 'transfers.index'
        },
        {
          name: 'Demand',
          href: route('transfers.demand.index'),
          route: 'transfers.demand.index'
        }
      ],
      visible: !isCompanyStakeholder()
    },
    {
      name: 'Customers',
      icon: CustomersIcon,
      children: [
        {
          name: 'Customers',
          href: route('customers.index'),
          route: 'customers.index'
        },
        {
          name: 'Check-In',
          href: route('customers.check-ins.index'),
          route: 'customers.check-ins.index'
        }
      ],
      visible: !isCompanyStakeholder()
    },
    {
      name: 'Ordering',
      icon: OrdersIcon,
      children: [
        {
          name: 'Orders',
          href: route('orders.index'),
          route: 'orders.index'
        },
        {
          name: 'Delivery',
          href: route('deliveries.index'),
          route: 'deliveries.index'
          // visible: hasPermission('view_deliveries'),
        },
        {
          name: 'Fulfillment',
          href: route('fulfillment.index'),
          route: 'fulfillment.index'
        }
      ],
      visible: !isCompanyStakeholder()
    },
    {
      name: 'Reports',
      icon: ReportingIcon,
      children: [
        {
          name: 'CR - Overview',
          href: route('reports.show', { report: 'closure_overview' }),
          route: 'reports.show',
          visible: isStory()
        },
        {
          name: 'CR - Register',
          href: route('reports.show', { report: 'closure_register' }),
          route: 'reports.show',
          visible: isStory()
        },
        {
          name: 'CR - Register by Day',
          href: route('reports.show', { report: 'closure_register_by_day' }),
          route: 'reports.show',
          visible: isStory()
        },
        {
          name: 'CR - Payments',
          href: route('reports.show', { report: 'closure_payments' }),
          route: 'reports.show',
          visible: isStory()
        },
        {
          name: 'CR - Discounted Transactions',
          href: route('reports.show', { report: 'closure_discounted' }),
          route: 'reports.show',
          visible: isStory()
        },
        {
          name: 'CR - Voids',
          href: route('reports.show', { report: 'closure_voided' }),
          route: 'reports.show',
          visible: isStory()
        },
        {
          name: 'CR - Cancellations',
          href: route('reports.show', { report: 'closure_cancelled' }),
          route: 'reports.show',
          visible: isStory()
        },

        {
          name: 'CR - Breakdown',
          href: route('reports.show', { report: 'closure_breakdown' }),
          route: 'reports.show',
          visible: isStory()
        },
        {
          name: 'CR - Sales By Customer Type',
          href: route('reports.show', { report: 'closure_by_customer_type' }),
          route: 'reports.show',
          visible: isStory()
        },
        {
          name: 'CR - Sales By Category',
          href: route('reports.show', { report: 'closure_by_category' }),
          route: 'reports.show',
          visible: isStory()
        },
        {
          name: 'CR - Totals by Fee Donation',
          href: route('reports.show', { report: 'closure_by_type' }),
          route: 'reports.show',
          visible: isStory()
        },
        {
          name: 'Sales Report',
          href: route('reports.show', { report: 'sales_simple' }),
          route: 'reports.show',
          visible: isStory()
        },
        {
          name: 'Current Inventory',
          href: route('reports.show', { report: 'updated_inventory_current' }),
          route: 'reports.show',
          visible: isStory()
        },
        {
          name: 'Inventory Received Report - Detailed',
          href: route('reports.show', { report: 'inventory_received_detailed' }),
          route: 'reports.show',
          visible: isStory()
        },
        {
          name: 'Inventory Received Report - Summary',
          href: route('reports.show', { report: 'inventory_received_summary' }),
          route: 'reports.show',
          visible: isStory()
        },
        {
          name: 'Inventory Adjustments',
          href: route('reports.show', { report: 'updated_adjustment_summary' }),
          route: 'reports.show',
          visible: isCompanyAdmin() && isStory()
        },
        {
          name: 'Sales Summary',
          href: route('reports.show', { report: 'sales_summary' }),
          route: 'reports.show',
          visible: isCompanyAdminOrManager() && !isStory()
        },
        {
          name: 'Tax Report - Detailed',
          href: route('reports.show', { report: 'sales_tax' }),
          route: 'reports.show',
          visible: isStory()
        },
        {
          name: 'Tax Report - Summary',
          href: route('reports.show', { report: 'tax_summary' }),
          route: 'reports.show',
          visible: isStory()
        },
        {
          name: 'Daily Journal',
          // href: route('reports.sage.show'),
          // route: 'reports.sage.show',
          href: route('reports.show', { report: 'sage' }),
          route: 'reports.show',
          visible: isStory()
        },
        {
          name: 'Sales Summary',
          href: route('reports.show', { report: 'updated_sales_summary' }),
          route: 'reports.show',
          visible: isStory()
        },
        {
          name: 'Sales by Order',
          href: route('reports.show', { report: 'sales_individual' }),
          route: 'reports.show',
          visible: isStory()
        },
        {
          name: 'Sales by Customer',
          href: route('reports.show', { report: 'sales_by_customer' }),
          route: 'reports.show',
          visible: isStory()
        },
        {
          name: 'Sales Detailed',
          href: route('reports.show', { report: 'sales' }),
          route: 'reports.show',
          visible: isCompanyAdmin() && !isStory()
        },
        // @todo: Introduce after review
        // {
        //     name: 'Register Summary',
        //     href: route('reports.show', { report: 'register_summary' }),
        //     route: 'reports.show',
        //     visible: isCompanyAdmin() && ! isStory(),
        // },
        {
          name: 'Sales by Inventory',
          href: route('reports.show', { report: 'inventory_sales' }),
          route: 'reports.show',
          visible: isStory()
        },
        {
          name: 'Sales Reconciliation',
          href: route('reports.show', { report: 'sales_reconciliation_new' }),
          route: 'reports.show',
          visible: isCompanyAdmin() && !isStory()
        },
        // @todo: Introduce after review
        // {
        //     name: 'Transaction Summary',
        //     href: route('reports.show', { report: 'transaction_summary' }),
        //     route: 'reports.show',
        //     visible: isCompanyAdmin() && ! isStory(),
        // },
        {
          name: 'Transfer Reconciliation',
          href: route('reports.show', { report: 'transfer_reconciliation' }),
          route: 'reports.show',
          visible: isCompanyAdmin() && !isStory()
        },
        {
          name: 'In-Transit Transfer Reconciliation',
          href: route('reports.show', { report: 'in_transit_transfer_reconciliation' }),
          route: 'reports.show',
          visible: isCompanyAdmin() && !isStory()
        },
        {
          name: 'Transfer Summary',
          href: route('reports.show', { report: 'transfers' }),
          route: 'reports.show',
          visible: isCompanyAdmin() && !isStory()
        },
        {
          name: 'Inventory Detailed',
          href: route('reports.show', { report: 'inventory_detailed' }),
          route: 'reports.show',
          visible: isCompanyAdmin() && !isStory()
        },
        {
          name: 'Current Inventory',
          href: route('reports.show', { report: 'inventory_current' }),
          route: 'reports.show',
          visible: !isStory()
        },
        {
          name: 'Current Inventory By Space',
          href: route('reports.show', { report: 'inventory_space' }),
          route: 'reports.show',
          visible: !isStory()
        },
        {
          name: 'Adjustment Summary',
          href: route('reports.show', { report: 'updated_adjustment_summary' }),
          route: 'reports.show',
          visible: isCompanyAdmin() && !isStory()
        },
        {
          name: 'Aging Inventory',
          href: route('reports.aging-inventory.index'),
          route: 'reports.aging-inventory.index',
          visible: !isStory()
        },
        {
          name: 'Missing Item Allocations',
          href: route('reports.show', { report: 'missing_entries' }),
          route: 'reports.show',
          visible: !isStory()
        }
      ],
      visible: !isCompanyStakeholder()
    },
    {
      name: 'Settings',
      icon: SettingsIcon,
      children: [
        {
          name: 'Company',
          href: route('company.edit'),
          route: 'company.edit',
          visible: isCompanyAdminOrManager()
        },
        {
          name: 'Company',
          href: route('company.show'),
          route: 'company.show',
          visible: !isCompanyAdminOrManager()
        },
        {
          name: 'Locations',
          href: route('company.locations.index'),
          route: 'company.locations.index',
          childRoutes: ['company.locations.show', 'company.locations.edit']
        },
        {
          name: 'Spaces',
          href: route('company.spaces.index'),
          route: 'company.spaces.index',
          visible: hasPermission('view_spaces'),
          childRoutes: ['company.spaces.edit']
        },
        {
          name: 'Printers',
          href: route('company.printers.index'),
          route: 'company.printers.index',
          visible: hasPermission('view_printers')
        },
        {
          name: 'Brands',
          href: route('company.brands.index'),
          route: 'company.brands.index',
          visible: hasPermission('view_brands')
        },
        {
          name: 'Suppliers',
          href: route('company.suppliers.index'),
          route: 'company.suppliers.index',
          visible: hasPermission('view_suppliers'),
          childRoutes: ['company.suppliers.edit']
        },
        {
          name: 'Strains',
          href: route('company.strains.index'),
          route: 'company.strains.index',
          visible: hasPermission('view_strains')
        },
        {
          name: 'Vehicles',
          href: route('company.vehicles.index'),
          route: 'company.vehicles.index',
          visible: hasPermission('view_vehicles')
        },
        {
          name: 'Users',
          href: route('company.users.index'),
          route: 'company.users.index',
          childRoutes: ['company.users.show', 'company.users.edit'],
          visible: isCompanyAdminOrManager()
        }
      ],
      visible: !isCompanyStakeholder()
    },
    {
      name: 'Integrations',
      href: route('company.integrations.index'),
      route: 'company.integrations.index',
      icon: IntegrationsIcon,
      visible: !isCompanyStakeholder()
    },
    // {
    //     name: 'Robotics',
    //     href: route('robotics.index'),
    //     route: 'robotics.index',
    //     icon: RoboticsIcon,
    // },
    // {
    //     name: 'Support',
    //     href: route('support'),
    //     route: 'support',
    //     icon: SupportIcon,
    //     // onClick() {
    //     //     window.loadZendeskWidget();
    //     // },
    // },
    {
      name: 'Support',
      icon: SupportIcon,
      children: [
        {
          name: 'Resources',
          href: route('support'),
          route: 'support'
        },
        {
          name: 'Feature Requests',
          href: route('feedback', { board: 'feature' }),
          route: 'feedback',
          isFullPageLoad: true
        },
        {
          name: 'Bugs',
          href: route('feedback', { board: 'bug' }),
          route: 'feedback',
          isFullPageLoad: true
        }
      ]
    }
  ]
}

export const getNavigation = (route, context) => {
  const config = navConfig(route, context)
  const nav = prune(config)

  return nav.map((item) => {
    if (item.children) {
      item.children = prune(item.children)
    }
    return item
  })
}

const prune = (items) => items.filter((item) => item?.visible ?? true)

export default navConfig
